/**
 * /v1/images —— images-manager 圖片清單（依 game_code 取該遊戲全部圖片 + access descriptor）。
 *
 * baseURL 走 http.ts 預設（runtimeConfig API_BASE）。回傳 = GameImage[]。
 * URL 組法（cdn/bucket domain + signed path/query）見 buildImageURL（另檔）。
 */
import { useHttp } from '../http'
import type { FetchImagesParams, FetchImagesPublicParams, GameImage, ImagesData } from './types'

const maps = {
  /** GET /v1/images/external?game_code=XGC（登入 token）。 */
  images: (): string => '/v1/images/external',
  /** GET /v1/images/public?game_code=&operator_id=（回放，公開、不帶 token）。 */
  imagesPublic: (): string => '/v1/images/public',
} as const

/**
 * 取某遊戲的圖片清單。
 *
 * @param params.gameCode 遊戲代碼（打 API 時轉大寫，例 'XGC'）
 * @returns GameImage[]（每張含 relative_path + 可選 access）
 * @throws ApiError 401 / 業務錯
 */
export const fetchImages = async (params: FetchImagesParams): Promise<GameImage[]> => {
  // useHttp 已拆共用 envelope CommonResponse<T> 的 .data → 這裡 T = ImagesData，再取 .images。
  const data = await useHttp.get<ImagesData>(maps.images(), {
    game_code: params.gameCode.toUpperCase(),
  })
  return data?.images ?? []
}

/**
 * 取某遊戲圖片清單（**回放模式**）—— 公開端點，**不帶 token**、`skipAuth`（不需登入）。
 * 回傳同 `fetchImages`（GameImage[]，含 tag + 可選 access）。
 *
 * @throws ApiError 業務錯
 */
export const fetchImagesPublic = async (
  params: FetchImagesPublicParams,
): Promise<GameImage[]> => {
  const data = await useHttp.get<ImagesData>(
    maps.imagesPublic(),
    {
      game_code: params.gameCode.toUpperCase(),
      operator_id: params.operatorId,
    },
    { skipAuth: true },
  )
  return data?.images ?? []
}
